import DynamicImage from '@/components/commons/dynamic-image';
import ShoppingCartIcon from '@/components/icons/shopping-cart-icon';
import { CartPopup } from '@/components/sections/builder/elements/header/header/cart-dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import useModal from '@/hooks/useModal';
import { addToCart } from '@/lib/actions/cart/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { queryClient } from '@/lib/query-client';
import { cn, formatIDR } from '@/lib/utils';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import { useCartProduct } from '@/stores/cart';
import { useProductStore } from '@/stores/product';
import { BaseCart } from '@/types/http/cart/common';
import { IProduct, ProductItem } from '@/types/product';
import { Loader2, Minus, Plus, Share2 } from 'lucide-react';
import React from 'react';
import { shallow } from 'zustand/shallow';
import Cookies from 'js-cookie';

type FixedAddToCartProps = {
  product: IProduct;
  theme?: ThemeType;
  preview?: boolean;
};

export default function FixedAddToCart({ product, theme, preview }: FixedAddToCartProps) {
  const host = window.location.host;
  const customerToken = Cookies.get('accessToken');
  const popupDrawer = useModal();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [cartPayload, setCartPayload] = React.useState<BaseCart | null>(null);

  const cartStore = useCartProduct((state) => state, shallow);
  const productStore = useProductStore((state) => state, shallow);
  const productTheme = useBuilderStore((state) => state.theme.productDetailButton, shallow);

  const themeContent = preview ? productTheme : theme?.productDetailButton;

  const availableQty = product.available_qty as number;
  const productVariants = product.variants;
  const quantity = productStore.quantity as number;
  const cartQty = cartStore.quantity as number;
  const productImage = productVariants
    ? productStore.imageVariant?.[0]?.assets_url
    : product.product_assets?.[0]?.assets_url;
  const productName =
    productVariants && quantity > 0 && cartStore.item_name
      ? cartStore.item_name.replace(/[^a-zA-Z0-9]/g, ' ')
      : product.product_name;

  const getMinMaxPrice = (productItems: ProductItem[]) => {
    const prices = productItems
      .map((item) => item.sell_price)
      .filter((price) => typeof price === 'number' && !isNaN(price)) as number[];

    if (!prices || prices.length === 0) return { minPrice: null, maxPrice: null };
    return { minPrice: Math.min(...prices), maxPrice: Math.max(...prices) };
  };
  const { minPrice, maxPrice } = getMinMaxPrice(product.product_items || []);
  const calculatePrice = () => {
    if (productVariants) {
      if (minPrice !== maxPrice) {
        return productStore.price ? productStore.price : minPrice;
      }
      return minPrice;
    } else {
      return product.product_items?.[0].sell_price as number;
    }
  };
  const calculateDiscountPrice = () => {
    if (productVariants) {
      return productStore.discountPrice;
    }
    return product.discount_price;
  };
  const price = calculatePrice();
  const discountPrice = calculateDiscountPrice();
  const totalPrice = React.useMemo(() => {
    return cartQty * Number(price);
  }, [cartQty, price]);
  const totalDiscountPrice = React.useMemo(() => {
    return cartQty * Number(discountPrice);
  }, [cartQty, discountPrice]);

  const incrementQty = React.useCallback(() => {
    const newQty = cartQty + 1;
    if (newQty <= availableQty) {
      cartStore.setQuantity(newQty);
    }
  }, [cartQty, availableQty, cartStore]);
  const decrementQty = React.useCallback(() => {
    const newQty = cartQty - 1;
    if (newQty >= 1) {
      cartStore.setQuantity(newQty);
    }
  }, [cartQty, cartStore]);
  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseInt(e.target.value.replace(/\D/g, ''), 10) || 1;
    const maxQty = availableQty;
    const clampedQty = Math.max(1, Math.min(numericValue, maxQty));

    cartStore.setQuantity(clampedQty);
  };

  const handleAddToCart = async () => {
    try {
      setIsLoading(true);

      if (cartQty === 0) {
        toast({
          title: 'Error',
          description: 'Product is out of stock',
          variant: 'destructive',
        });
        return;
      }

      const selectedItem = productVariants ? cartStore : product.product_items?.[0];

      if (!selectedItem) {
        toast({
          title: 'Error',
          description: 'No product item available',
          variant: 'destructive',
        });
        return;
      }

      const {
        item_id: id,
        sell_price,
        discount_price,
        item_weight,
        item_height,
        item_width,
        item_length,
      } = selectedItem;

      const item = {
        id: id || '',
        product_name: product.product_name,
        sell_price: Number(sell_price),
        discount_price: Number(discount_price),
        product_assets: product.product_assets,
        quantity: cartQty,
        variants: cartStore.variants,
        sku: cartStore.sku,
        item_weight: Number(item_weight),
        item_height: Number(item_height),
        item_width: Number(item_width),
        item_length: Number(item_length),
        total_value: Number(sell_price) * cartQty,
      };

      const payload = {
        id: item.id || '',
        quantity: item.quantity,
      };

      await addToCart(host as string, payload, customerToken);

      setCartPayload(item);
      setTimeout(() => popupDrawer.open(), 1000);

      toast({
        title: 'Success',
        duration: 1000,
        description: 'Product has been added to cart',
        variant: 'success',
      });

      cartStore.setQuantity(quantity > 0 ? 1 : 0);

      queryClient.invalidateQueries({ queryKey: ['carts'] });

      SendEventAnalytic('add_to_cart', {
        currency: 'IDR',
        value: item.sell_price * item.quantity,
        items: [
          {
            item_name: item.product_name,
            item_id: item.id,
            price: Number(item.sell_price),
            quantity: item.quantity,
            unique_action_id: Date.now(),
          },
        ],
      });

      SendEventMetapixel('AddToCart', {
        content_ids: [item.id],
        content_name: item.product_name,
        content_category: product?.category_name,
        content_type: 'product',
        quantity: item.quantity,
        value: item.sell_price * item.quantity,
        currency: 'IDR',
      });
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    await navigator.clipboard.writeText(url);
    toast({
      description: 'Copied to clipboard',
      variant: 'success',
      duration: 1000,
    });
  };

  return (
    <>
      <div className='flex w-full flex-col items-center justify-between gap-2 sm:flex-col md:flex-col lg:max-h-[100px] lg:flex-row lg:gap-10'>
        <div className='flex w-full items-center gap-3'>
          <DynamicImage
            src={(productImage as string) ?? (product.product_assets?.[0].assets_url as string)}
            alt={product.product_name ?? 'Product Image'}
            width={60}
            height={60}
            sizes='(max-width: 768px) 100vw, 600px'
            className='h-[60px] w-[60px] rounded border object-cover'
          />
          <div className='flex flex-col gap-1'>
            <span className='line-clamp-1 text-base md:text-base lg:text-lg xl:text-lg'>
              {productName}
            </span>
            <span
              className={cn(
                'text-sm md:text-sm lg:text-base xl:text-base',
                quantity === 0 && availableQty === 0 ? 'text-[#f44336]' : 'text_product_secondary'
              )}
            >
              Stock: {quantity > 0 ? quantity : availableQty}
            </span>
          </div>
        </div>

        <div className='flex w-full flex-row-reverse items-center justify-between gap-6 lg:flex-row'>
          <div className='flex items-center space-x-4 rounded-lg p-2'>
            <Button
              variant='default'
              onClick={decrementQty}
              className='theme_button_modal_product_detail'
              disabled={
                cartQty <= 1 || isLoading || (productVariants && !productStore.allSelectedVariants)
              }
            >
              <Minus className='h-2 w-2' />
            </Button>
            <Input
              type='text'
              value={cartQty}
              onChange={handleQtyChange}
              max={quantity}
              min={1}
              className={cn(
                'w-[60px] border-none text-center text-sm font-semibold focus-visible:ring-0 md:text-base lg:text-lg'
              )}
              disabled={
                cartQty === 0 || isLoading || (productVariants && !productStore.allSelectedVariants)
              }
            />
            <Button
              variant='default'
              onClick={incrementQty}
              className='theme_button_modal_product_detail'
              disabled={
                cartQty === 0 ||
                cartQty >= quantity ||
                isLoading ||
                (productVariants && !productStore.allSelectedVariants)
              }
            >
              <Plus className='h-2 w-2' />
            </Button>
          </div>

          <div className='flex-1 whitespace-nowrap'>
            {(discountPrice as number) > 0 && (
              <div className='text_product_secondary text-lg font-bold'>
                {formatIDR(totalDiscountPrice ?? discountPrice)}
              </div>
            )}
            <div
              className={cn(
                (discountPrice as number) > 0
                  ? 'text-sm font-normal text-[#C2C2C2] line-through'
                  : 'text-lg font-bold'
              )}
            >
              {totalPrice > 0 ? formatIDR(totalPrice) : formatIDR(price as number)}
            </div>
          </div>
        </div>

        <div className='flex w-full flex-row-reverse items-center gap-4 gap-x-2 md:flex-row lg:flex-row'>
          <Button
            className='theme_button_modal_product_detail inline-flex flex-1 items-center gap-x-3 text-xs lg:text-[14px]'
            size='lg'
            onClick={handleAddToCart}
            disabled={
              cartQty === 0 || isLoading || (productVariants && !productStore.allSelectedVariants)
            }
          >
            {isLoading ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <>
                <ShoppingCartIcon className='h-5 w-5' />
                <span>{themeContent?.buttonLabelAddToCart}</span>
              </>
            )}
          </Button>
          <Button variant='ghost' size='icon' className='shrink-0' onClick={handleCopyLink}>
            <Share2 className='h-5 w-5' />
          </Button>
        </div>
      </div>

      {theme?.cart?.cartType === 'popup_notification' && popupDrawer.isShow && (
        <CartPopup
          openModal={popupDrawer.isShow}
          cart={cartPayload}
          setOpenModal={popupDrawer.setIsShow}
          host={window.location.host}
        />
      )}
    </>
  );
}
